import {Button, Select} from "antd";
import styled from "styled-components";
import {CoachliLogoPurpleIcon} from "../../../pages/dashboard/automations/icons/coachli-logo-purple-icon";
import {GoogleMeetIcon} from "../../../pages/dashboard/automations/icons/google-meet-icon";
import {IS_PHYSICAL_LOCATION_FEATURE_ENABLED} from "../../../services/config";

export type LocationValue =
  | "video"
  | "phone"
  | "google_meet"
  | "custom_url"
  | "physical_location";

interface LocationSelectProps {
  value: LocationValue | undefined;
  onChange: (value: LocationValue) => void;
  calendarConnected: boolean;
  onConnectCalendar: () => void;
  isConnectingCalendar?: boolean;
  /** "session" (default) → video, phone, google_meet. "live-event" → video, google_meet, custom_url, physical_location */
  variant?: "session" | "live-event";
}

const StyledInfoBox = styled.div<{variant: "info" | "error"}>`
  margin-top: 1.2rem;
  padding: 1.2rem 1.6rem;
  border-radius: 6px;
  border: 1px solid ${({variant}) => (variant === "info" ? "#B2E0D8" : "#FCA5A5")};
  background-color: ${({variant}) => (variant === "info" ? "#EBF9F6" : "#FEF2F2")};
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  font-size: 1.4rem;
  color: #222;
  line-height: 1.5;

  a {
    color: inherit;
    font-weight: 600;
    text-decoration: underline;
  }
`;

const StyledInlineLinkButton = styled(Button)`
  &.ant-btn-link {
    height: auto;
    padding: 0;
    border: none;
    color: inherit;
    font-size: inherit;
    font-weight: 600;
    text-decoration: underline;
  }

  &.ant-btn-link:hover,
  &.ant-btn-link:focus {
    color: inherit;
  }
`;

const StyledLocationSelect = styled(Select)`
  .ant-select-selector {
    display: flex;
    align-items: center;
  }

  .ant-select-selection-item {
    display: flex;
    align-items: center;
    line-height: 1;
  }

  .ant-select-item-option-content {
    display: flex;
    align-items: center;
  }
` as typeof Select;

const StyledOptionContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  line-height: 1;
`;

const StyledLocationIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;

  svg,
  img {
    width: 2rem;
    height: 2rem;
  }
`;

const StyledSmallLocationIcon = styled(StyledLocationIcon)`
  width: 2rem;
  height: 2rem;

  svg,
  img {
    width: 2rem;
    height: 2rem;
    max-width: 2rem;
    max-height: 2rem;
  }
`;

function CoachliVideoIcon() {
  return (
    <StyledLocationIcon>
      <CoachliLogoPurpleIcon />
    </StyledLocationIcon>
  );
}

function PhoneLocationIcon() {
  return (
    <StyledLocationIcon>
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="20" height="20" rx="10" fill="#222222" />
        <path
          d="M14.725 12.3189V14.1753C14.7251 14.3083 14.6747 14.4362 14.5841 14.5335C14.4935 14.6307 14.3693 14.69 14.2367 14.6993C14.0073 14.715 13.8199 14.7234 13.675 14.7234C9.03557 14.7234 5.27499 10.9629 5.27499 6.32344C5.27499 6.17854 5.28287 5.99111 5.29914 5.76169C5.30845 5.6291 5.3677 5.50497 5.46494 5.41435C5.56218 5.32374 5.69018 5.27338 5.82309 5.27344H7.67949C7.74461 5.27337 7.80743 5.29751 7.85575 5.34117C7.90407 5.38483 7.93443 5.44489 7.94094 5.50969C7.95302 5.63044 7.96404 5.72651 7.97454 5.79949C8.07888 6.52762 8.2927 7.2358 8.60874 7.90001C8.65862 8.00501 8.62607 8.13049 8.53157 8.19769L7.39862 9.00724C8.09133 10.6213 9.37763 11.9076 10.9917 12.6003L11.8002 11.4695C11.8333 11.4233 11.8815 11.3902 11.9365 11.3759C11.9914 11.3616 12.0497 11.367 12.101 11.3913C12.7652 11.7067 13.4732 11.92 14.201 12.0239C14.274 12.0344 14.3701 12.0459 14.4898 12.0575C14.5545 12.0641 14.6144 12.0945 14.658 12.1428C14.7015 12.1911 14.7256 12.2539 14.7255 12.3189H14.725Z"
          fill="white"
        />
      </svg>
    </StyledLocationIcon>
  );
}

function GoogleMeetLocationIcon() {
  return (
    <StyledLocationIcon>
      <GoogleMeetIcon />
    </StyledLocationIcon>
  );
}

export function CustomUrlLocationIcon() {
  return (
    <StyledSmallLocationIcon>
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12.145 10.0233L11.0845 8.96127L12.145 7.90077C12.4957 7.5531 12.7743 7.13959 12.9648 6.68396C13.1553 6.22834 13.2539 5.73958 13.255 5.24574C13.2561 4.7519 13.1596 4.26272 12.9711 3.80626C12.7826 3.34981 12.5059 2.93508 12.1567 2.58588C11.8075 2.23668 11.3927 1.9599 10.9363 1.77141C10.4798 1.58293 9.99063 1.48646 9.49679 1.48754C9.00296 1.48862 8.5142 1.58723 8.05857 1.77771C7.60295 1.9682 7.18943 2.2468 6.84177 2.59752L5.78127 3.65877L4.72002 2.59827L5.78202 1.53777C6.76663 0.553152 8.10206 0 9.49452 0C10.887 0 12.2224 0.553152 13.207 1.53777C14.1916 2.52238 14.7448 3.85781 14.7448 5.25027C14.7448 6.64273 14.1916 7.97815 13.207 8.96277L12.1458 10.0233H12.145ZM10.024 12.1443L8.96277 13.2048C7.97815 14.1894 6.64273 14.7425 5.25027 14.7425C3.85781 14.7425 2.52238 14.1894 1.53777 13.2048C0.553152 12.2202 1.46719e-08 10.8847 0 9.49227C-1.46719e-08 8.09981 0.553152 6.76438 1.53777 5.77977L2.59902 4.71927L3.65952 5.78127L2.59902 6.84177C2.2483 7.18943 1.9697 7.60295 1.77921 8.05857C1.58873 8.51419 1.49012 9.00296 1.48904 9.49679C1.48796 9.99063 1.58443 10.4798 1.77291 10.9363C1.9614 11.3927 2.23818 11.8075 2.58738 12.1567C2.93658 12.5059 3.35131 12.7826 3.80776 12.9711C4.26422 13.1596 4.7534 13.2561 5.24724 13.255C5.74108 13.2539 6.22984 13.1553 6.68546 12.9648C7.14109 12.7743 7.5546 12.4957 7.90227 12.145L8.96277 11.0845L10.024 12.145V12.1443ZM9.49302 4.18902L10.5543 5.25027L5.25102 10.5528L4.18977 9.49227L9.49302 4.18977V4.18902Z"
          fill="#222222"
        />
      </svg>
    </StyledSmallLocationIcon>
  );
}

function PhysicalLocationIcon() {
  return (
    <StyledSmallLocationIcon>
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.523 11.5229L6.75 16.2959L1.977 11.5229C1.03301 10.5789 0.39014 9.3762 0.129696 8.06683C-0.130747 6.75746 0.00293013 5.40026 0.513824 4.16687C1.02472 2.93347 1.88988 1.87927 2.99991 1.13757C4.10994 0.395878 5.41498 0 6.75 0C8.08502 0 9.39006 0.395878 10.5001 1.13757C11.6101 1.87927 12.4753 2.93347 12.9862 4.16687C13.4971 5.40026 13.6308 6.75746 13.3703 8.06683C13.1099 9.3762 12.467 10.5789 11.523 11.5229V11.5229ZM6.75 9.74994C7.54565 9.74994 8.30871 9.43387 8.87132 8.87126C9.43393 8.30865 9.75 7.54559 9.75 6.74994C9.75 5.95429 9.43393 5.19123 8.87132 4.62862C8.30871 4.06601 7.54565 3.74994 6.75 3.74994C5.95435 3.74994 5.19129 4.06601 4.62868 4.62862C4.06607 5.19123 3.75 5.95429 3.75 6.74994C3.75 7.54559 4.06607 8.30865 4.62868 8.87126C5.19129 9.43387 5.95435 9.74994 6.75 9.74994V9.74994ZM6.75 8.24994C6.35218 8.24994 5.97065 8.09191 5.68934 7.8106C5.40804 7.5293 5.25 7.14777 5.25 6.74994C5.25 6.35212 5.40804 5.97059 5.68934 5.68928C5.97065 5.40798 6.35218 5.24994 6.75 5.24994C7.14783 5.24994 7.52936 5.40798 7.81066 5.68928C8.09197 5.97059 8.25 6.35212 8.25 6.74994C8.25 7.14777 8.09197 7.5293 7.81066 7.8106C7.52936 8.09191 7.14783 8.24994 6.75 8.24994Z"
          fill="#222222"
        />
      </svg>
    </StyledSmallLocationIcon>
  );
}

function InfoCircleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      style={{flexShrink: 0, marginTop: 1}}
    >
      <circle cx="9" cy="9" r="9" fill="#2DB6A3" />
      <rect x="8.25" y="7.5" width="1.5" height="6" rx="0.75" fill="#fff" />
      <circle cx="9" cy="5.5" r="0.9" fill="#fff" />
    </svg>
  );
}

function ErrorCircleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      style={{flexShrink: 0, marginTop: 1}}
    >
      <circle cx="9" cy="9" r="9" fill="#EF4444" />
      <line
        x1="5.5"
        y1="5.5"
        x2="12.5"
        y2="12.5"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <line
        x1="12.5"
        y1="5.5"
        x2="5.5"
        y2="12.5"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

type OptionConfig = {
  value: LocationValue;
  label: string;
  Icon: () => JSX.Element;
};

const SESSION_OPTIONS: OptionConfig[] = [
  {value: "video", label: "Coachli video", Icon: CoachliVideoIcon},
  {value: "phone", label: "Phone call", Icon: PhoneLocationIcon},
  {value: "google_meet", label: "Google meet", Icon: GoogleMeetLocationIcon},
];

const LIVE_EVENT_OPTIONS: OptionConfig[] = [
  {value: "video", label: "Coachli video", Icon: CoachliVideoIcon},
  {value: "google_meet", label: "Google meet", Icon: GoogleMeetLocationIcon},
  {value: "custom_url", label: "Custom URL", Icon: CustomUrlLocationIcon},
  {value: "physical_location", label: "Physical location", Icon: PhysicalLocationIcon},
];

function getLiveEventOptions(): OptionConfig[] {
  return LIVE_EVENT_OPTIONS.filter(
    (option) =>
      option.value !== "physical_location" || IS_PHYSICAL_LOCATION_FEATURE_ENABLED
  );
}

function InfoMessage({
  value,
  calendarConnected,
  onConnectCalendar,
  isConnectingCalendar,
}: Pick<
  LocationSelectProps,
  "value" | "calendarConnected" | "onConnectCalendar" | "isConnectingCalendar"
>) {
  if (!value) return null;

  if (value === "video") {
    return (
      <StyledInfoBox variant="info">
        <InfoCircleIcon />
        <span>
          You and your customers will be sent a link to join the video meeting room
        </span>
      </StyledInfoBox>
    );
  }

  if (value === "phone") {
    return (
      <StyledInfoBox variant="info">
        <InfoCircleIcon />
        <span>
          Your customer will be required to provide their phone number at checkout
        </span>
      </StyledInfoBox>
    );
  }

  if (value === "google_meet") {
    if (calendarConnected) {
      return (
        <StyledInfoBox variant="info">
          <InfoCircleIcon />
          <span>Meeting details will be sent to your customers after booking</span>
        </StyledInfoBox>
      );
    }
    return (
      <StyledInfoBox variant="error">
        <ErrorCircleIcon />
        <span>
          Google calendar isn&apos;t connected. Connect your calendar in order to use
          Google meet as a location.{" "}
          <StyledInlineLinkButton
            type="link"
            onClick={onConnectCalendar}
            loading={isConnectingCalendar}
          >
            Connect Google calendar
          </StyledInlineLinkButton>
        </span>
      </StyledInfoBox>
    );
  }

  if (value === "physical_location" && IS_PHYSICAL_LOCATION_FEATURE_ENABLED) {
    return (
      <StyledInfoBox variant="info">
        <InfoCircleIcon />
        <span>Enter the venue address where your customers should show up</span>
      </StyledInfoBox>
    );
  }

  return null;
}

export function LocationSelect({
  value,
  onChange,
  calendarConnected,
  onConnectCalendar,
  isConnectingCalendar,
  variant = "session",
}: LocationSelectProps) {
  const options = variant === "live-event" ? getLiveEventOptions() : SESSION_OPTIONS;
  return (
    <div>
      <StyledLocationSelect<LocationValue>
        value={value}
        onChange={onChange}
        size="large"
        style={{width: "100%"}}
        placeholder="Please select a location"
        optionLabelProp="label"
      >
        {options.map(({value: optValue, label, Icon}) => (
          <Select.Option
            key={optValue}
            value={optValue}
            label={
              <StyledOptionContent>
                <Icon />
                <span>{label}</span>
              </StyledOptionContent>
            }
          >
            <StyledOptionContent>
              <Icon />
              <span>{label}</span>
            </StyledOptionContent>
          </Select.Option>
        ))}
      </StyledLocationSelect>
      <InfoMessage
        value={value}
        calendarConnected={calendarConnected}
        onConnectCalendar={onConnectCalendar}
        isConnectingCalendar={isConnectingCalendar}
      />
    </div>
  );
}
